import React, { Fragment, useState } from "react";
import { useDispatch, useSelector } from "react-redux"
import { FormattedMessage } from "react-intl";
import { Popover } from "@material-ui/core";
import Radio, { RadioGroup } from "@sterling-react/radio";
import { InfoOutlined } from "@material-ui/icons";
import { IconButton } from "@sterling-react/button";
import { MfaPageContainer } from "./MfaPageContainer";
import { loginSetFlowStageWatcher } from "../../redux/actions/loginactioncreators";
import flowStages from "../../flowStages";
import { navigateToLogin } from "../../redux/helpers/navigators";
import { MfaType } from "./model/MfaType";
import { skipMfaSetupComplete } from "../../redux/actions/loginactioncreators";
import PropTypes from "prop-types";

function ItemWithInformationPopover(props) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const mfaItemPopoverId = open ? `${props.type}-mfaItemPopoverId` : undefined;
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget.parentNode);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const Label = () => props.typeLabel;
    const PopoverText = () => props.typePopover;
    return (
        <span>
            <Label  />
            <IconButton
                aria-describedby={mfaItemPopoverId}
                onClick={handleClick}
                variant="text"
                className={"w-6"}
            >
                <InfoOutlined className="sm:px-1" />
            </IconButton>
            <Popover
                id={mfaItemPopoverId}
                onClose={handleClose}
                open={open}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
            >
                <div className="sm:p-2 sm:max-w-sm bg-white sm:ml-0 p-4">
                    <PopoverText />
                </div>
            </Popover>
        </span>
    )
}
ItemWithInformationPopover.propTypes = {
    typePopover: PropTypes.element,
    type: PropTypes.string,
    typeLabel: PropTypes.string,
}
function MfaSkipInfoButton() {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget.parentNode);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const open = Boolean(anchorEl);
    const mfaOptOutInfoId = open ? "multiFactorAuthOptOutInfo" : undefined;

    return (
        <Fragment>
            <IconButton
                aria-describedby={mfaOptOutInfoId}
                onClick={handleClick}
                variant="text"
                className={"w-6"}
            >
                <InfoOutlined className="sm:px-1" />
            </IconButton>
            <Popover
                id={mfaOptOutInfoId}
                onClose={handleClose}
                open={open}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
            >
                <div className="sm:p-2 sm:max-w-sm bg-white sm:ml-0 p-4">
                    <FormattedMessage
                        id="mfa.skip"
                        defaultMessage="The security of your account is important to us. Enabling Multi-factor Authentication is a fast and easy way to keep your information secure. The new level of security will let you rest easy knowing that your information is safe, and it only takes a few minutes to set up!"
                        description="A message meant to discourage users from opting out of Multi-factor Authentication"
                    />
                </div>
            </Popover>
        </Fragment>
    )
}
export default function MfaSetup() {
    const defaultMfaType = MfaType.TOTP;

    const [mfaTypeSelected, setMfaTypeSelected] = useState(defaultMfaType);
    const dispatch = useDispatch();
    const mfaTotpEnabled = useSelector((state) => state.loginForm.mfaFactorAuthenticatorEnabled);
    const mfaSmsEnabled = useSelector((state) => state.loginForm.mfaFactorSMSEnabled);
    const mfaEmailEnabled = useSelector((state) => state.loginForm.mfaFactorEmailEnabled);
    const mfaChallenge = useSelector((state)=>state.loginForm.challenge);
    const setFlowStage = (payload) => { dispatch(loginSetFlowStageWatcher(payload)) };
    const hideOption = (mfaType) => {
        const allowedMfaOptions = mfaChallenge?.challengeParameters?.["MFA_OPTIONS"] ?? [];
        const setting = allowedMfaOptions[mfaType.toUpperCase()];
        return setting && setting.toUpperCase() === "DISABLED";
    }
    const {email, email_verified} = useSelector((state) => state.loginForm.userInfo?.UserAttributes) ?? {};

    const mfaTypeArray = [
        {
            type: MfaType.TOTP,
            label: <FormattedMessage id="mfaType.totp" defaultMessage="Authenticator App (Recommended)" description="The option to use time-based one time passwords generated by software running on a mobile device" />,
            flowStage: flowStages.MFA_SETUP_TOTP,
            hide: !mfaTotpEnabled || hideOption(MfaType.TOTP)
        },
        {
            type: MfaType.SMS,
            label: <FormattedMessage id="mfaType.sms" defaultMessage="Text message sent to your device" description="The option to use one time passwords received by text message to a mobile device" />,
            flowStage: flowStages.MFA_SETUP_SMS,
            hide: !mfaSmsEnabled || hideOption(MfaType.SMS)
        },
        {
            type: MfaType.EMAIL,
            test: (email && email_verified === "true"),
            label: <FormattedMessage id="mfaType.email" defaultMessage="Emailed to you" description="The option to receive one time passwords by email" />,
            disabledLabel: (
                <ItemWithInformationPopover
                    type={MfaType.EMAIL}
                    typeLabel=<FormattedMessage id="mfaType.email" defaultMessage="Emailed to you" description="The option to receive one time passwords by email" />
                    typePopover=<FormattedMessage id="mfa.emailNotVerifiedText" defaultMessage="Email not verified. Verify your email address in account settings." description="" />
                />
            ),
            flowStage: flowStages.MFA_SETUP_EMAIL,
            hide: !mfaEmailEnabled || hideOption(MfaType.EMAIL)
        },
        {
            type: MfaType.SKIP,
            flowStage: flowStages.SKIP,
            hide: hideOption(MfaType.SKIP),
            label: (
                <span className="">
                    <FormattedMessage id="mfaType.skip" defaultMessage="Opt out of Multi-Factor Authentication" description="Option to opt out of multi-factor authentication" />
                    <MfaSkipInfoButton />
                </span>)
        },
    ]

    const handleClickBack = () => {
        navigateToLogin();
    }
    const handleSubmit = () => {
        if(mfaTypeSelected === MfaType.SKIP) {
            dispatch(skipMfaSetupComplete());
        }
        const flowStage = mfaTypeArray.find(x => x.type === mfaTypeSelected).flowStage;
        if (flowStage) {
            setFlowStage({ flowStage });
        }
    }

    const MfaTypes = (props) => {
        const onclickFn = (e) => {
            if (typeof props.onClick === "function") {
                props.onClick(e.target.value);
            }
        }
        return (
            <RadioGroup
                divClassName=""
                ariaLabel={"Choose multi-factor authentication"}
                name="mfaType"
                defaultValue={props.defaultValue}
                labelClassName=""
            >
                {props.mfaTypeArray.filter((item)=>!item.hide)
                .map((item) => {
                    const { type, label, test, disabledLabel } = item;
                    if (test === undefined || test) {
                        return (
                            <Radio
                                key={type}
                                id={type + "Radio"}
                                labelClassName="mx-1"
                                value={type}
                                onClick={onclickFn}
                                label={label}
                                checked={type === mfaTypeSelected}
                            />
                        )
                    } else {
                        return (
                            <Radio
                                key={type}
                                id={type + "Radio"}
                                labelClassName="mx-1"
                                value={type}
                                label={disabledLabel ?? label}
                                disabled={true}
                            />
                        )
                    }
                })}
            </RadioGroup>
        )
    }
    MfaTypes.propTypes = {
        onClick: PropTypes.func.isRequired,
        defaultValue: PropTypes.string.isRequired,
        mfaTypeArray: PropTypes.arrayOf(PropTypes.string).isRequired,
    }

    return (
        <MfaPageContainer
            onSubmit={handleSubmit}
            showErrors={true}
            headerText={<FormattedMessage id="mfaSetup.choiceTitle" defaultMessage="Let's verify it's really you" description="The title text for a page with a list of multi-factor authentication choices" />}
            instructionalText={<FormattedMessage id="mfaSetup.enticeBlurb" defaultMessage="Help us prevent unauthorized access to your personal information. How would you like to receive a verification code?" description="Instructions for selecting a multi-factor authentication option" />}
            submitButtonText={<FormattedMessage id="mfaSetup.continueButton" defaultMessage="Continue" description="Submit multi-factor authentication selection" />}
            secondaryButtonText={<FormattedMessage id="mfaSetup.backButton" defaultMessage="Back" description="Return to login" />}
            secondaryButtonHandler={handleClickBack}
            otpValidator={true}
        >
            <MfaTypes
                id="mfaTypeRadioGroup"
                defaultValue={defaultMfaType}
                mfaTypeArray={mfaTypeArray}
                onClick={setMfaTypeSelected}
            />
        </MfaPageContainer>
    )
}
